import { ReactElement } from "react";

import { groupBy } from "@/lib/groupBy";
import {
  ColumnDef,
  ColumnFiltersState,
  ColumnOrderState,
  ExpandedState,
  Row,
  RowData,
  SortingState,
  VisibilityState,
} from "@tanstack/react-table";

export interface DatatableState {
  showColumnHeaders: boolean;
  sectionColumnId?: string | null | undefined;

  subSectionColumnId?: string | null | undefined;

  // tanstack table state
  globalFilter: string;
  columnFilters: ColumnFiltersState;
  columnOrder: ColumnOrderState;
  columnVisibility: VisibilityState;
  sorting: SortingState;
  expanded: ExpandedState;
}

export const defaultDatatableState: DatatableState = {
  showColumnHeaders: true,
  sectionColumnId: null,
  subSectionColumnId: null,

  globalFilter: "",
  columnFilters: [],
  columnOrder: [],
  columnVisibility: {},
  sorting: [],
  expanded: true,
};

// List of valid keys from DatatableState
const validKeys: Array<keyof DatatableState> = [
  "showColumnHeaders",
  "sectionColumnId",
  "subSectionColumnId",
  "globalFilter",
  "columnFilters",
  "columnOrder",
  "columnVisibility",
  "sorting",
  "expanded",
];

// Helper function to filter and clean an object
function filterValidKeys<T extends object>(obj: T): Partial<T> {
  if (typeof obj !== "object" || obj === null) {
    return obj; // Return primitive values as-is
  }

  if (Array.isArray(obj)) {
    return obj.map((item) =>
      typeof item === "object" && item !== null ? filterValidKeys(item) : item,
    ) as unknown as Partial<T>; // Recursively process arrays
  }

  const result: Partial<T> = {};
  for (const key in obj) {
    if (validKeys.includes(key as keyof DatatableState) && !key.startsWith("_")) {
      const value = obj[key as keyof T];
      if (typeof value === "object" && value !== null) {
        // Only pass objects to the recursive function
        result[key as keyof T] = filterValidKeys(value) as T[keyof T];
      } else {
        // Assign primitive values directly
        result[key as keyof T] = value;
      }
    }
  }
  return result;
}

// Function to compare two DatatableState objects
export function compareDatatableStates(state1: DatatableState, state2?: DatatableState | null | undefined): boolean {
  if (!state2) {
    return false;
  }

  const cleanedState1 = filterValidKeys(state1);
  const cleanedState2 = filterValidKeys(state2);

  return JSON.stringify(cleanedState1) === JSON.stringify(cleanedState2);
}

export type DatatableFilterConfigurationViewType = "none" | "popover" | "modal";

export interface DatatableColumn<TData extends RowData, TValue = unknown> {
  columnId: string;

  // the order for the column among all columns in the table ascending from left to right
  getOrderValue: () => number;

  // the default visibility state of the column (visible or hidden by default)
  getDefaultVisibilityState: () => boolean;

  // the column definition for the column: This is where the column is defined for react-table
  // here you define the column's id, accessorKey, enableHiding, enableSorting, enableColumnFilter, sortingFn, filterFn, header, cell, etc.
  getColumnDef: () => ColumnDef<TSectionData<TData>, TValue>;

  // The name of the column as it is displayed to the user (e.g. when toggling column visibility)
  getDisplayValue: () => string;

  // The view rendered to display the column in the list of available filters
  renderDisplayView: () => ReactElement;

  // Whether to render the filter configuration view in a modal or dropdown
  getFilterConfigurationViewType: () => DatatableFilterConfigurationViewType;

  // The view to render (in the modal or dropdown) where the user enters or selects the filter value(s)
  renderFilterConfigurationView: (onFinished: () => void) => ReactElement | null;

  // The view to render in the list of applied filters
  renderAppliedFilterView: () => ReactElement;

  // Whether to enable sectioning for the column
  enableSectioning?: boolean;

  // The key to use for sectioning the column
  // If not provided, the column will not be sectioned
  getSectionKey?: (data: TData) => string;

  // The view to render in the section header
  renderSectionHeader?: (data: SectionData<TData>, row: Row<SectionData<TData>>) => ReactElement;

  // The view to render in the double section header
  renderSubSectionHeader?: (data: SubSectionData<TData>, row: Row<SubSectionData<TData>>) => ReactElement;
}

export interface SectionData<TData extends RowData> {
  columnId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  column: DatatableColumn<TData, any>;
  key: string;
  data: TData[] | SubSectionData<TData>[];
  getSectionKey: (data: TData) => string;
}

export interface SubSectionData<TData extends RowData> {
  columnId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  column: DatatableColumn<TData, any>;
  key: string;
  data: TData[];
  getSubSectionKey: (data: TData) => string;
}

export type TSectionData<TData extends RowData> = TData | SectionData<TData> | SubSectionData<TData>;

export function groupRowsBySection<TData extends RowData>(
  data: TData[],
  columnsConfig: DatatableColumn<TData>[],
  sectionColumnId: string,
  subSectionColumnId?: string | null,
): TSectionData<TData>[] {
  const sectionColumn = columnsConfig.find((c) => c.columnId === sectionColumnId);
  if (!sectionColumn?.enableSectioning || !sectionColumn.getSectionKey) {
    return data;
  }

  const getSectionKey = sectionColumn.getSectionKey;

  const sectionGroups: SectionData<TData>[] = Array.from(groupBy(data, getSectionKey).entries()).map(
    ([key, items]) => ({
      columnId: sectionColumnId,
      key,
      column: sectionColumn,
      data: items,
      getSectionKey,
    }),
  );

  if (!subSectionColumnId) {
    return sectionGroups;
  }

  const subSectionColumn = columnsConfig.find((c) => c.columnId === subSectionColumnId);
  if (!subSectionColumn?.enableSectioning || !subSectionColumn.getSectionKey) {
    return sectionGroups;
  }

  const getSubSectionKey = subSectionColumn.getSectionKey;

  const sectionGroupsWithSubSections: SectionData<TData>[] = sectionGroups.map((section) => {
    const subGroups: SubSectionData<TData>[] = Array.from(
      groupBy(section.data as TData[], getSubSectionKey).entries(),
    ).map(([subKey, subItems]) => ({
      columnId: subSectionColumnId,
      key: subKey,
      column: subSectionColumn, // Still a DatatableColumn<TData>, safe for all rows
      data: subItems,
      getSubSectionKey,
    }));

    return {
      columnId: section.columnId,
      key: section.key,
      column: section.column, // Still a DatatableColumn<TData>
      data: subGroups, // SubSectionData<TData>[]
      getSectionKey,
    };
  });

  return sectionGroupsWithSubSections;
}

export function isRawRow<TData extends RowData>(row: TSectionData<TData>): row is TData {
  return row !== null && typeof row === "object" && !("data" in row);
}

export function isSectionRow<TData extends RowData>(row: TSectionData<TData>): row is SectionData<TData> {
  return row !== null && typeof row === "object" && "data" in row && Array.isArray(row.data);
}

export function isSubSectionRow<TData extends RowData>(row: TSectionData<TData>): row is SubSectionData<TData> {
  return (
    row !== null &&
    typeof row === "object" &&
    "data" in row &&
    Array.isArray(row.data) &&
    row.data.length > 0 &&
    row.data[0] !== null &&
    typeof row.data[0] === "object" &&
    !("data" in row.data[0])
  );
}
